/* eslint-disable react/prop-types */
import { Button } from '@blueprintjs/core'
import { useGetPolotnoFonts } from '../../../api/polotno/useGetPolotnoFonts'
import { observer } from 'mobx-react-lite'
import { registerNextDomDrop } from 'polotno/canvas/page'
import { StoreType } from 'polotno/model/store'
import { ImagesGrid } from 'polotno/side-panel/images-grid'
import { t } from 'polotno/utils/l10n'
import styled from 'polotno/utils/styled'
import React, { useEffect, useState } from 'react'
import { POLOTNO_KEY } from '../../../config'
import { TextOverlay } from './customPolotno/customTextOverlay'
import { Switch } from 'antd'
import { useGlobalContext } from '../../../context/globalContext'
import { SectionTab } from 'polotno/side-panel'
import { TextSmallcapsIcon } from './hugeIcons/hugeIcons'
import './polotnoTextPanel.less'

// ─── Polotno styled helpers (used by Custom Text / Fonts tabs) ────────────────

const Container = styled('div')`
  height: calc(100% - 40px);
  display: flex;
  flex-direction: column;

  .bp5-dark & .polotno-text-preview-plain {
    filter: invert(1);
  }
`

const toBase64 = (file: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload  = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })

// ─── Types ────────────────────────────────────────────────────────────────────

type TabId = 'text-overlay' | 'text' | 'font'

interface FontItemProps {
  onSelect: () => void
  onRemove: () => void
  font: { fontFamily: string }
}

// ─── FontItem ─────────────────────────────────────────────────────────────────

const FontItem = observer(({ onSelect, onRemove, font }: FontItemProps) => (
  <div className="text-panel__font-card" style={{ fontFamily: font.fontFamily }} onClick={onSelect}>
    {font.fontFamily} text
    <Button
      style={{ position: 'absolute', right: 6, bottom: 6 }}
      minimal
      small
      icon="trash"
      onClick={(e) => { e.stopPropagation(); onRemove() }}
    />
  </div>
))

// ─── DragButton ───────────────────────────────────────────────────────────────

interface DragButtonProps { onSelect: (position?: { x: number; y: number }) => void }

const DragButton: React.FC<DragButtonProps> = ({ onSelect, ...props }) => (
  <Button
    {...props}
    draggable
    className="polotno-close-panel"
    onClick={() => onSelect()}
    onDragStart={() => { registerNextDomDrop(({ x, y }) => { onSelect({ x, y }) }) }}
    onDragEnd={() => { registerNextDomDrop(null) }}
  />
)

// ─── TextPanel ────────────────────────────────────────────────────────────────

export const TextPanel = observer(({ store }: { store: StoreType }) => {
  React.useEffect(() => { store.loadFont('Roboto') }, [])

  const {
    ShowTextOverlay:  [showTextOverlay, setShowTextOverlay],
    ActiveSidePanel:  [, setActiveSidePanel],
  } = useGlobalContext()

  useEffect(() => { setActiveSidePanel(store.openedSidePanel) }, [])

  const [tab, selectTab] = useState<TabId>('text-overlay')

  const { data } = useGetPolotnoFonts(`${POLOTNO_KEY}`)

  // ── Toggle: show/hide all h1-notlinked overlays ──
  const handleTextOverlayToggle = (checked: boolean) => {
    setShowTextOverlay(checked)
    store.pages.forEach((page: any) => {
      page.children.forEach((child: any) => {
        if (child.name === 'h1-notlinked') {
          child.set({ visible: checked, opacity: checked ? 1 : 0 })
        }
      })
    })
  }

  const handleFileInput = async (e: { target: any }) => {
    const { target } = e
    for (const file of target.files) {
      const url = await toBase64(file)
      store.addFont({ fontFamily: (file.name as string).split('.')[0] })
    }
    target.value = null
  }

  React.useEffect(() => {
    store.fonts.forEach((font) => store.loadFont(font.fontFamily))
  }, [store.fonts])

  const TABS: { id: TabId; label: string }[] = [
    { id: 'text-overlay', label: 'Text' },
    { id: 'text',         label: 'Custom Text' },
    { id: 'font',         label: 'Custom Fonts' },
  ]

  return (
    <div className="text-panel">

      {/* ── Tab bar ──────────────────────────────────────────────────── */}
      <div className="text-panel__tabs">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            className={`text-panel__tab${tab === id ? ' text-panel__tab--active' : ''}`}
            onClick={() => selectTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Text Overlay tab ─────────────────────────────────────────── */}
      {tab === 'text-overlay' && (
        <div className="text-panel__body">

          {/* Toggle card */}
          <div className="text-panel__toggle-card">
            <div>
              <p className="text-panel__toggle-label">Display text on screen</p>
              <p className="text-panel__toggle-hint">Show or hide text overlays across all scenes</p>
            </div>
            <Switch
              checked={showTextOverlay}
              onChange={handleTextOverlayToggle}
              size="small"
            />
          </div>

          {/* Scene rows */}
          <div className="text-panel__scene-list">
            {store.pages.map((page: any, index: number) => (
              <div key={page.id} className="text-panel__scene-row">
                <div className="text-panel__scene-header">
                  <span className="text-panel__scene-badge">{index + 1}</span>
                  <span className="text-panel__scene-label">Scene {index + 1}</span>
                </div>
                <TextOverlay index={index} store={store} pages={page} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Custom Text tab ───────────────────────────────────────────── */}
      {tab === 'text' && (
        <Container>
          <ImagesGrid
            shadowEnabled={false}
            images={data?.items}
            getPreview={(image) => image.preview}
            getImageClassName={(image: { preview: string; json: string }) => {
              const isPlain = image.json.indexOf('plain') >= 0
              return isPlain ? 'polotno-text-preview-plain' : ''
            }}
            isLoading={!data}
            error={Error}
            onSelect={async (item, pos) => {
              const req  = await fetch(item.json)
              const json = await req.json()
              if (!store.activePage) return

              const baseSize    = 1080 + 1080
              const currentSize = store.width + store.height
              const scale       = currentSize / baseSize
              const dX = pos ? pos.x - (json.width / 2) * scale : store.width  / 2 - (json.width  / 2) * scale
              const dY = pos ? pos.y - (json.height / 2) * scale : store.height / 2 - (json.height / 2) * scale

              store.history.transaction(() => {
                const ids: any[] = []
                json.pages[0].children.forEach((text: any) => {
                  delete text.id
                  const { id } = store.activePage?.addElement({
                    ...text,
                    type:     'text',
                    fontSize: text.fontSize * scale,
                    x:        text.x * scale + dX,
                    y:        text.y * scale + dY,
                    width:    text.width  * scale,
                    height:   text.height * scale,
                  }) as any
                  ids.push(id)
                })
                store.selectElements(ids)
              })
            }}
          />
        </Container>
      )}

      {/* ── Custom Fonts tab ─────────────────────────────────────────── */}
      {tab === 'font' && (
        <div className="text-panel__body" style={{ gap: 0 }}>
          <label htmlFor="polotno-font-upload">
            <Button
              icon="upload"
              className="text-panel__upload-btn"
              style={{ width: '100%' }}
              onClick={() => {
                ;(document.querySelector('#polotno-font-upload') as HTMLElement)?.click()
              }}
            >
              {t('sidePanel.uploadFont')}
            </Button>
            <input
              type="file"
              accept=".ttf, .otf, .woff, .woff2, .eot"
              id="polotno-font-upload"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </label>

          <div className="text-panel__font-grid">
            {store.fonts.map((font, i) => (
              <FontItem
                key={i}
                font={font}
                onSelect={() => {
                  store.loadFont(font.fontFamily)
                  store.pages.forEach((page) => {
                    page.children.forEach((element) => {
                      if (element.type === 'text')
                        store.getElementById(element.id)?.set({ fontFamily: font.fontFamily })
                    })
                  })
                }}
                onRemove={() => store.removeFont(font.fontFamily)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
})

// ─── Panel registration ───────────────────────────────────────────────────────

export const CustomTextPanelPolotno = {
  name: 'text-panel',
  Tab: (props: any) => (
    <SectionTab name="Text" {...props}>
      <TextSmallcapsIcon className="sidebar-panel-tab-icon" />
    </SectionTab>
  ),
  Panel: TextPanel,
}
